"use client";

import { Dialog } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";

export default function DeleteAlert({
  isOpen,
  onClose,
  onConfirm,
  title = "common.deleteConfirm",
  description = "common.deleteConfirmation",
  isValidationRequired = false,
  validationValue = "",
  validationPlaceholder = "",
  buttonText = "common.delete",
  isLoading = false,
}) {
  const { t } = useLanguage();
  const [inputValue, setInputValue] = useState("");

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
                <ExclamationTriangleIcon
                  aria-hidden="true"
                  className="size-6 text-red-600"
                />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <Dialog.Title
                  as="h3"
                  className="text-base font-semibold text-gray-900"
                >
                  {t(title)}
                </Dialog.Title>
                <div className="mt-2">
                  <p
                    className="text-sm text-gray-500"
                    dangerouslySetInnerHTML={{ __html: t(description) }}
                  />
                </div>
              </div>
            </div>
            {isValidationRequired && (
              <div className="mt-5 sm:ml-10 sm:mt-4 sm:flex sm:pl-4">
                <input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  type="text"
                  placeholder={t(validationPlaceholder)}
                  className={`w-full border border-gray-300 rounded-md p-2 
                    ${validationValue !== inputValue ? "border-red-300" : ""}`}
                />
              </div>
            )}
            <div className="mt-5 sm:ml-10 sm:mt-4 sm:flex sm:pl-4">
              <button
                type="button"
                onClick={onConfirm}
                disabled={
                  (isValidationRequired && validationValue !== inputValue) ||
                  isLoading
                }
                className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white sm:w-auto
                  ${(isValidationRequired && validationValue !== inputValue) || isLoading ? "bg-gray-300" : "bg-red-600"} ${isLoading ? "cursor-not-allowed" : "hover:bg-red-500"}`}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t("common.deleting")}
                  </>
                ) : (
                  t(buttonText)
                )}
              </button>
              <button
                type="button"
                data-autofocus
                onClick={onClose}
                disabled={isLoading}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:ml-3 sm:mt-0 sm:w-auto disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {t("common.cancel")}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
